import apiClient from "./apiClient";

/**
 * In-house integration builder — /api/v1/org/in-house (org admins only).
 *
 * Uses the raw client rather than `apiRequest` because the builder needs the
 * structured error body: 409 carries `dependents` for unpublish/disable, and
 * 422 carries per-field `fields` messages for the test form.
 *
 * Every function resolves (never rejects) to
 *   { success: true,  status, data }
 *   { success: false, status, message, fields, dependents }
 */

const BASE = "/api/v1/org/in-house";

// Import and test calls reach customer servers; give them more than the
// client-wide 10 s default.
const SLOW_TIMEOUT_MS = 45000;

const FALLBACK_BY_STATUS = {
  401: "Your session has expired. Please sign in again.",
  403: "Only organization admins can change in-house integrations.",
  404: "That integration was not found.",
  409: "That can't be done right now.",
  422: "Some details need fixing."
};

/** FastAPI returns `detail` as a string, an object, or a list of validation errors. */
const readError = (error) => {
  const status = error.response?.status ?? 0;
  const detail = error.response?.data?.detail;
  let message = FALLBACK_BY_STATUS[status] || "Something went wrong. Please try again.";
  let fields = null;
  let dependents = null;
  // FastAPI's bare "Not Found" means the route itself is missing (backend not
  // updated yet), not a missing integration; say so instead of echoing it.
  if (status === 404 && detail === "Not Found") {
    message = "The in-house builder isn't available on this server yet.";
  } else if (typeof detail === "string") message = detail;
  else if (Array.isArray(detail))
    message = detail.map((d) => (d.msg || "").replace(/^Value error, /, "")).join("; ");
  else if (detail && typeof detail === "object") {
    message = detail.message || message;
    fields = detail.fields || null;
    dependents = detail.dependents || null;
  }
  if (!error.response) {
    // No response at all: a timeout, the server being down, or a crash the browser
    // hides (an error without CORS headers). Say which where we can tell.
    message =
      error.code === "ECONNABORTED"
        ? "Orbit's server took too long to answer. It may be busy; please try again."
        : "Couldn't reach Orbit's server. Check your connection and try again; if it keeps happening, the server may have hit an error.";
  }
  return { success: false, status, message, fields, dependents };
};

const call = async (request) => {
  try {
    const response = await request();
    return { success: true, status: response.status, data: response.data };
  } catch (error) {
    return readError(error);
  }
};

const slow = { timeout: SLOW_TIMEOUT_MS };

// Every other builder request. Each makes several database round trips (saving the API
// makes about 15), which from a laptop on a remote database can pass the client-wide
// 10 s default; a save cut off there may still finish on the server, so waiting longer
// beats telling the admin it failed.
const patient = { timeout: 30000 };

export const listInHouse = () => call(() => apiClient.get(BASE, patient));
export const getInHouse = (id) => call(() => apiClient.get(`${BASE}/${id}`, patient));
export const createInHouse = (body) => call(() => apiClient.post(BASE, body, patient));
export const updateInHouse = (id, body) =>
  call(() => apiClient.patch(`${BASE}/${id}`, body, patient));

export const previewSpec = (source) =>
  call(() => apiClient.post(`${BASE}/spec-preview`, source, slow));
export const previewImport = (id, source) =>
  call(() => apiClient.post(`${BASE}/${id}/api/import`, source, slow));
export const commitImport = (id, body) =>
  call(() => apiClient.post(`${BASE}/${id}/api/import/commit`, body, slow));

export const defineApi = (id, body) =>
  call(() => apiClient.put(`${BASE}/${id}/api`, body, patient));
export const saveConnection = (id, body) =>
  call(() => apiClient.put(`${BASE}/${id}/connection`, body, patient));
/** With `draft` ({ api, credentials }) checks what the form shows without saving it. */
export const checkConnection = (id, draft) =>
  call(() => apiClient.post(`${BASE}/${id}/connection/check`, draft || null, slow));

/** Saves the API site's own icon as the logo; with no url the server uses the saved base URL. */
export const setLogoFromSite = (id, url) =>
  call(() => apiClient.post(`${BASE}/${id}/logo/from-site`, url ? { url } : {}, slow));
// In-house integrations are providers, so an uploaded logo goes through the providers route.
export const uploadLogo = (id, file) => {
  const form = new FormData();
  form.append("file", file);
  return call(() => apiClient.post(`/api/v1/providers/${id}/logo`, form, patient));
};

export const createEndpoint = (id, body) =>
  call(() => apiClient.post(`${BASE}/${id}/endpoints`, body, patient));
export const updateEndpoint = (id, endpointId, body) =>
  call(() => apiClient.patch(`${BASE}/${id}/endpoints/${endpointId}`, body, patient));
export const deleteEndpoint = (id, endpointId) =>
  call(() => apiClient.delete(`${BASE}/${id}/endpoints/${endpointId}`, patient));
export const publishEndpoint = (id, endpointId) =>
  call(() => apiClient.post(`${BASE}/${id}/endpoints/${endpointId}/publish`, null, patient));
export const unpublishEndpoint = (id, endpointId, confirm = false) =>
  call(() =>
    apiClient.post(`${BASE}/${id}/endpoints/${endpointId}/unpublish`, null, {
      ...patient,
      params: { confirm }
    })
  );
export const testEndpoint = (id, endpointId, payload) =>
  call(() =>
    apiClient.post(`${BASE}/${id}/endpoints/${endpointId}/test`, { payload }, slow)
  );
export const listFixtures = (id, endpointId) =>
  call(() => apiClient.get(`${BASE}/${id}/endpoints/${endpointId}/fixtures`, patient));
export const saveFixture = (id, endpointId, body) =>
  call(() =>
    apiClient.post(`${BASE}/${id}/endpoints/${endpointId}/fixtures`, body, patient)
  );
export const deleteFixture = (id, endpointId, fixtureId) =>
  call(() =>
    apiClient.delete(`${BASE}/${id}/endpoints/${endpointId}/fixtures/${fixtureId}`, patient)
  );

/** `confirm` acknowledges routes/workflows that would stop working. */
const lifecycle = (action) => (id, confirm = false) =>
  call(() =>
    apiClient.post(`${BASE}/${id}/${action}`, null, { ...patient, params: { confirm } })
  );
export const publishInHouse = lifecycle("publish");
export const unpublishInHouse = lifecycle("unpublish");
export const disableInHouse = lifecycle("disable");

/** What decommissioning would remove (writes nothing), then the permanent removal. */
export const getDecommissionImpact = (id) =>
  call(() => apiClient.get(`${BASE}/${id}/decommission`, patient));
export const decommissionInHouse = (id, confirmName, acknowledgeDependents = false) =>
  call(() =>
    apiClient.post(
      `${BASE}/${id}/decommission`,
      { confirm_name: confirmName, acknowledge_dependents: acknowledgeDependents },
      patient
    )
  );

/** One integration's trail, including deleted calls and connection changes. */
export const getAuditTrail = (integrationId, { limit = 200, offset = 0 } = {}) =>
  call(() =>
    apiClient.get("/api/v1/org/audit", {
      ...patient,
      params: { integration_id: integrationId, limit, offset }
    })
  );
